import {
  bordesDeVigencia,
  calcularSlots,
  type DuracionPersona,
  type EntradaMotor,
  type SlotDisponible,
} from '@practiclinic/core'
import { db } from './db.js'

/**
 * El motor de disponibilidad, alimentado desde Dexie.
 *
 * Esto es lo que §5.4 pide literalmente: **una sola implementación del motor,
 * usada por la PWA, la API y el sitio público**. Acá no se reimplementa nada
 * —`calcularSlots` es el mismo código que corre en el servidor—, sólo se
 * traducen las filas locales a las formas que espera.
 *
 * Y es lo que hace que agendar funcione **sin conexión**: la recepcionista
 * necesita ver los huecos aunque se haya caído internet, y pedirle los slots al
 * servidor en cada clic haría que la agenda deje de funcionar justo cuando más
 * se la necesita. La verdad final la sigue imponiendo Postgres al escribir
 * (§4.3): esto ofrece, no garantiza.
 */

export interface OpcionesSlots {
  servicioId: string
  desde: Date
  hasta: Date
  /** "Quiero con la Dra. X". Sólo aplica a tipos seleccionables por el cliente. */
  recursoPreferido?: string
  slotMin?: number
}

export async function slotsLocales(op: OpcionesSlots): Promise<SlotDisponible[]> {
  const servicio = await armarServicio(op.servicioId)
  if (!servicio) return []
  const duraciones = await cargarDuracionesLocales(op.servicioId)

  const [tipos, recursos, capacidades, horarios, bloqueos, afinidades, asignaciones, reservas, personaEspecialidad] =
    await Promise.all([
      db.recurso_tipo.toArray(),
      db.recurso.filter((r) => r.activo).toArray(),
      db.recurso_capacidad.toArray(),
      db.recurso_horario.toArray(),
      db.recurso_bloqueo.toArray(),
      db.recurso_afinidad.toArray(),
      db.asignacion_espacio
        .filter((a) => a.estado === 'publicada' && a.deleted_at === null)
        .toArray(),
      db.reserva_recurso.filter((r) => r.activa).toArray(),
      db.persona_especialidad.toArray(),
    ])

  const capsPorRecurso = new Map<string, string[]>()
  for (const c of capacidades) {
    const lista = capsPorRecurso.get(c.recurso_id) ?? []
    lista.push(c.capacidad)
    capsPorRecurso.set(c.recurso_id, lista)
  }

  const espPorRecurso = new Map<string, string[]>()
  for (const esp of personaEspecialidad) {
    const lista = espPorRecurso.get(esp.persona_id) ?? []
    lista.push(esp.especialidad_id)
    espPorRecurso.set(esp.persona_id, lista)
  }

  const entrada: EntradaMotor = {
    servicio,
    tipos: tipos.map((t) => ({
      id: t.id,
      esPersona: t.es_persona,
      esEspacio: t.es_espacio,
      seleccionablePorCliente: t.seleccionable_por_cliente,
      bufferMin: t.buffer_min,
    })),
    recursos: recursos.map((r) => ({
      id: r.id,
      tipoId: r.recurso_tipo_id,
      activo: r.activo,
      ...(capsPorRecurso.has(r.id) ? { capacidades: capsPorRecurso.get(r.id)! } : {}),
      ...(espPorRecurso.has(r.id) ? { especialidades: espPorRecurso.get(r.id)! } : {}),
    })),
    horarios: horarios.map((h) => ({
      recursoId: h.recurso_id,
      diaSemana: h.dia_semana,
      desde: h.desde,
      hasta: h.hasta,
    })),
    bloqueos: bloqueos.map((b) => ({
      recursoId: b.recurso_id,
      rango: { inicio: new Date(b.rango.inicio), fin: new Date(b.rango.fin) },
    })),
    afinidades: afinidades.map((a) => ({
      personaId: a.persona_id,
      espacioId: a.espacio_id,
      modo: a.modo,
      cedeSiAusente: a.cede_si_ausente,
    })),
    asignacionesEspacio: asignaciones.map((a) => ({
      id: a.id,
      personaId: a.persona_id,
      espacioId: a.espacio_id,
      diaSemana: a.dia_semana,
      desde: a.desde,
      hasta: a.hasta,
      ...bordesDeVigencia(a.vigencia),
    })),
    // Los rangos guardados ya traen el buffer materializado: se escribieron así
    // al crear la cita, no se recalculan al leer.
    ocupacion: reservas.map((r) => ({
      recursoId: r.recurso_id,
      rango: { inicio: new Date(r.rango.inicio), fin: new Date(r.rango.fin) },
    })),
    ventana: { inicio: op.desde, fin: op.hasta },
    slotMin: op.slotMin ?? 15,
    ...(op.recursoPreferido ? { recursoPreferido: op.recursoPreferido } : {}),
    ...(duraciones.length > 0 ? { duracionesPorPersona: duraciones } : {}),
  }

  return calcularSlots(entrada)
}

/**
 * Duración y buffer por profesional de los tramos de este servicio, con la
 * misma forma que arma la API (`agenda/cargar.ts`, `cargarDuracionesPorPersona`).
 * Va en la entrada del motor, no dentro del servicio.
 */
async function cargarDuracionesLocales(servicioId: string): Promise<DuracionPersona[]> {
  const tramos = await db.servicio_tramo.where('servicio_id').equals(servicioId).toArray()
  if (tramos.length === 0) return []
  const ordenPorTramo = new Map(tramos.map((t) => [t.id, t.orden]))
  const filas = await db.servicio_tramo_persona.where('servicio_tramo_id').anyOf([...ordenPorTramo.keys()]).toArray()
  return filas.map((f) => ({
    ordenTramo: ordenPorTramo.get(f.servicio_tramo_id)!,
    personaId: f.persona_id,
    duracionMin: f.duracion_min,
    bufferMin: f.buffer_min,
  }))
}

async function armarServicio(servicioId: string): Promise<EntradaMotor['servicio'] | null> {
  const servicio = await db.servicio.get(servicioId)
  if (!servicio) return null

  const tramos = await db.servicio_tramo.where('servicio_id').equals(servicioId).toArray()
  const requisitos = await db.servicio_tramo_req.toArray()

  return {
    id: servicio.id,
    tramos: tramos
      .sort((a, b) => a.orden - b.orden)
      .map((t) => ({
        orden: t.orden,
        duracionMin: t.duracion_min,
        // Un tramo sin requisitos es legítimo y ocupa tiempo igual (§4.4): el
        // paciente esperando que actúe la anestesia.
        requisitos: requisitos
          .filter((r) => r.servicio_tramo_id === t.id)
          .map((r) => ({
            recursoTipoId: r.recurso_tipo_id,
            cantidad: r.cantidad,
            ...(r.mismo_recurso_que_tramo !== null
              ? { mismoRecursoQueTramo: r.mismo_recurso_que_tramo }
              : {}),
            ...(r.capacidad_requerida !== null
              ? { capacidadRequerida: r.capacidad_requerida }
              : {}),
          })),
      })),
    ...(servicio.anticipacion_min_h !== null
      ? { anticipacionMinH: servicio.anticipacion_min_h }
      : {}),
    ...(servicio.anticipacion_max_d !== null
      ? { anticipacionMaxD: servicio.anticipacion_max_d }
      : {}),
    ...(servicio.especialidad_requerida_id !== null && servicio.especialidad_requerida_id !== undefined
      ? { especialidadRequeridaId: servicio.especialidad_requerida_id }
      : {}),
  }
}

/** La asignación en el formato que espera el outbox. */
export function aCuerpoAsignacion(slot: SlotDisponible) {
  return slot.tramos.map((t) => ({
    orden: t.orden,
    inicio: t.rango.inicio.toISOString(),
    fin: t.rango.fin.toISOString(),
    asignacionEspacioId: t.asignacionEspacioId ?? null,
    profesionalPrincipalId: t.profesionalPrincipalId ?? null,
    recursos: t.recursos.map((r) => ({
      recursoId: r.recursoId,
      tipoId: r.tipoId,
      inicio: r.rango.inicio.toISOString(),
      fin: r.rango.fin.toISOString(),
    })),
  }))
}
